/* eslint-disable react/no-unescaped-entities */
'use client';

import { validPhonePattern } from '@ahm/common-helpers';
import { FloatingLabelInput } from '@ahm/react-ui/components/floating-label-fields';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Space, Stack, Text, rem } from '@mantine/core';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import type { RegisterRequestBody } from '../api';
import { useAuth } from '../auth-provider';
import { AuthTab } from '../lib/constants';
import { usePathname, useSearchParams } from '../lib/navigation';
import { authLocales, createTranslations, type Locales } from '../locales';
import { useAuthStore } from '../store/auth-store-provider';

type SignUpFormProps = {
  asPage: boolean;
  lang?: keyof Locales;
};

export default function SignUpForm({
  asPage = false,
  lang = 'vi',
}: SignUpFormProps) {
  const searchParams = useSearchParams();
  const phoneNumberSearchParam = searchParams.get('phoneNumber');
  const refCodeSearchParam =
    searchParams.get('ref') ??
    searchParams.get('ref_code') ??
    searchParams.get('referral_code') ??
    undefined;
  const tAuth = createTranslations({
    locales: authLocales,
    lang,
  });

  const pathname = usePathname();

  const { apiError, phoneNumber, authTabScreen } = useAuthStore(
    (state) => state
  );

  const { loading, onRegister, registerData } = useAuth();

  const SignUpSchema = z.object({
    name: z
      .string({ required_error: tAuth('requiredName') })
      .refine((value) => value.trim() !== '', {
        message: tAuth('requiredName'),
      }),
    mobile: z
      .string({ required_error: tAuth('requiredPhoneNumber') })
      .refine((value) => validPhonePattern.test(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .refine((value) => value.trim() !== '', {
        message: tAuth('requiredPhoneNumber'),
      }),
    email: z
      .string({ required_error: tAuth('requiredEmail') })
      .email('Invalid email address')
      .refine((value) => value.trim() !== '', {
        message: tAuth('requiredEmail'),
      }),
    referral_code: z.string().optional(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    setFocus,
  } = useForm<RegisterRequestBody>({
    resolver: zodResolver(SignUpSchema),
    defaultValues: {
      name: registerData.name ?? '',
      mobile: phoneNumber! ?? phoneNumberSearchParam,
      email: registerData.email ?? '',
      country_code: 'VN',
      referral_code: refCodeSearchParam,
    },
  });

  useEffect(() => {
    if (
      authTabScreen === AuthTab.SignUp ||
      pathname === AuthTab.SignUp.toString()
    ) {
      setFocus('name');
    }
  }, [authTabScreen, setFocus, pathname]);

  useEffect(() => {
    if (!asPage) {
      phoneNumber && setValue('mobile', phoneNumber);
      registerData?.name && setValue('name', registerData?.name);
      registerData?.email && setValue('email', registerData?.email);
    }
    if (asPage) {
      const nameSearchParam = searchParams.get('name') ?? '';
      const emailSearchParam = searchParams.get('email') ?? '';
      phoneNumberSearchParam && setValue('mobile', phoneNumberSearchParam);
      nameSearchParam && setValue('name', nameSearchParam);
      emailSearchParam && setValue('email', emailSearchParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, phoneNumberSearchParam, registerData]);

  const onSubmit = (values: RegisterRequestBody) => {
    onRegister(values);
  };

  return (
    <Box
      display="flex"
      w="100%"
      flex={1}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      pb="xs"
      style={{ flexDirection: 'column' }}
    >
      <Stack align="stretch" flex={1}>
        {/* <FocusTrap active={pathname === AuthTab.SignIn.toString()}> */}
        <Controller
          name="name"
          control={control}
          render={({ field: { value, onChange, ...props } }) => (
            <FloatingLabelInput
              {...props}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              size="sm"
              autoComplete="name"
              label={tAuth('fullName')}
              placeholder={tAuth('namePlaceholder')}
              value={value}
              onChange={(value) => onChange(value)}
            />
          )}
        />
        {/* </FocusTrap> */}

        <Controller
          name="mobile"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FloatingLabelInput
              size="sm"
              autoComplete="tel"
              label={tAuth('phoneNumber')}
              placeholder={tAuth('phonePlaceholder')}
              value={value}
              onChange={(value) => onChange(value)}
              error={
                apiError?.code.includes('MOBILE') &&
                (apiError?.description ??
                  apiError?.title ??
                  errors.mobile?.message)
              }
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FloatingLabelInput
              size="sm"
              autoComplete="email"
              label="Email"
              placeholder={tAuth('emailPlaceholder')}
              value={value}
              onChange={(value) => onChange(value)}
              error={
                apiError?.code.includes('EMAIL') &&
                (apiError?.description ??
                  apiError?.title ??
                  errors.email?.message)
              }
            />
          )}
        />

        <Controller
          name="referral_code"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FloatingLabelInput
              size="sm"
              autoComplete="code"
              label={
                <>
                  {tAuth('referralCode')}{' '}
                  <Text size="sm" component="span" c="neutral.5">
                    ({tAuth('optional')})
                  </Text>
                </>
              }
              placeholder={tAuth('referralPlaceholder')}
              value={value}
              onChange={(value) => onChange(value)}
              error={
                apiError?.code.includes('EMAIL') &&
                (apiError?.description ??
                  apiError?.title ??
                  errors.email?.message)
              }
            />
          )}
        />

        <Space h={rem(32)} w="100%" />

        <Text size="xs" c="neutral.6" fw={500} mt="auto">
          {tAuth('privacyPolicyAndTermsOfService')}
          <Text
            component="a"
            c="primary.6"
            fw={600}
            td="underline"
            href="https://www.ahamove.com/policy"
            target="_blank"
          >
            {tAuth('privacyPolicyAndTermsOfServiceLink')}
          </Text>
        </Text>

        <Button
          type="submit"
          fullWidth
          disabled={!isValid}
          loading={loading}
          px="md"
          h={44}
        >
          {tAuth('signUp')}
        </Button>
      </Stack>
    </Box>
  );
}
